/**
 * Stocky の Stocktakes 一覧をブラウザでコピーした TSV を読む。
 *
 * CSV 本体には棚卸し No. / Name / Location / Generated at / Status が無いため、
 * 一覧の1行を別の来歴として結び付ける。列位置を決め打ちせず見出しで解決し、
 * 読めない行や日時は推測しない。
 */

const MAX_PASTE_BYTES = 256 * 1024;
const MAX_ROWS = 10_000;

const HEADER_ALIASES = {
  stockyNumber: ["no.", "no", "number", "番号"],
  name: ["name", "名前"],
  locationName: ["location", "拠点"],
  generatedAt: ["generated at", "generated", "作成日時", "生成日時"],
  status: ["status", "状態"],
} as const;

type RequiredHeader = keyof typeof HEADER_ALIASES;

export interface StockyStocktakeListMetadata {
  /** 貼り付け内でだけ使う安定キー。サーバーへは送らない。 */
  id: string;
  stockyNumber: string;
  name: string;
  locationName: string;
  generatedAtRaw: string;
  /** Stocky の timezone 情報なしローカル日時。読めなければ null。 */
  generatedAtLocal: string | null;
  generatedOn: string | null;
  status: string;
  /** 監査用。ブラウザから受け取った見出しと該当行を改変せず保持する。 */
  submittedHeaderText: string;
  submittedRowText: string;
}

export type StockyStocktakeListParseError =
  | "too_large"
  | "header_not_found"
  | "duplicate_header"
  | "duplicate_stocky_number"
  | "too_many_rows"
  | "no_data_rows";

export interface StockyStocktakeListParseResult {
  rows: StockyStocktakeListMetadata[];
  error: StockyStocktakeListParseError | null;
  skippedRows: number;
  invalidDateRows: number;
}

function normalizedHeader(value: string): string {
  return value
    .normalize("NFKC")
    .replace(/\u00a0/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("en-US");
}

function headerKind(value: string): RequiredHeader | null {
  const normalized = normalizedHeader(value);
  for (const [kind, aliases] of Object.entries(HEADER_ALIASES) as Array<
    [RequiredHeader, readonly string[]]
  >) {
    if (aliases.includes(normalized)) return kind;
  }
  return null;
}

function validCalendarDate(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): boolean {
  if (
    year < 1900 ||
    year > 9999 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    second < 0 ||
    second > 59
  ) {
    return false;
  }
  return new Date(Date.UTC(year, month - 1, day)).getUTCDate() === day;
}

function dateParts(
  raw: string,
):
  | [
      year: number,
      month: number,
      day: number,
      hour: number,
      minute: number,
      second: number,
    ]
  | null {
  const value = raw.normalize("NFKC").trim();
  // Stocky の実画面で確認した表示は DD/MM/YYYY HH:mm。timezone は表示されない。
  const stocky = value.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+|,\s*)(\d{1,2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (stocky) {
    return [
      Number(stocky[3]),
      Number(stocky[2]),
      Number(stocky[1]),
      Number(stocky[4]),
      Number(stocky[5]),
      Number(stocky[6] ?? 0),
    ];
  }
  // 将来 Stocky が ISO 表示へ変わっても意味が一意な形式だけは受ける。
  const iso = value.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T|\s)(\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (!iso) return null;
  return [
    Number(iso[1]),
    Number(iso[2]),
    Number(iso[3]),
    Number(iso[4]),
    Number(iso[5]),
    Number(iso[6] ?? 0),
  ];
}

export function normalizeStockyGeneratedAt(raw: string): {
  generatedAtLocal: string;
  generatedOn: string;
} | null {
  const parts = dateParts(raw);
  if (!parts || !validCalendarDate(...parts)) return null;
  const [year, month, day, hour, minute, second] = parts;
  const pad = (value: number) => String(value).padStart(2, "0");
  const generatedOn = `${String(year).padStart(4, "0")}-${pad(month)}-${pad(day)}`;
  return {
    generatedOn,
    generatedAtLocal: `${generatedOn}T${pad(hour)}:${pad(minute)}:${pad(second)}`,
  };
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

export function parseStockyStocktakeList(
  text: string,
): StockyStocktakeListParseResult {
  if (byteLength(text) > MAX_PASTE_BYTES) {
    return { rows: [], error: "too_large", skippedRows: 0, invalidDateRows: 0 };
  }
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  let headerLine = -1;
  let headerIndexes: Record<RequiredHeader, number> | null = null;
  let duplicateHeader = false;

  for (const [lineIndex, line] of lines.entries()) {
    const cells = line.split("\t");
    const found = new Map<RequiredHeader, number>();
    let duplicate = false;
    for (const [cellIndex, cell] of cells.entries()) {
      const kind = headerKind(cell);
      if (!kind) continue;
      if (found.has(kind)) duplicate = true;
      found.set(kind, cellIndex);
    }
    if (duplicate) {
      duplicateHeader = true;
      continue;
    }
    if (
      (Object.keys(HEADER_ALIASES) as RequiredHeader[]).every((kind) =>
        found.has(kind),
      )
    ) {
      headerLine = lineIndex;
      headerIndexes = Object.fromEntries(found) as Record<
        RequiredHeader,
        number
      >;
      break;
    }
  }

  if (!headerIndexes) {
    return {
      rows: [],
      error: duplicateHeader ? "duplicate_header" : "header_not_found",
      skippedRows: 0,
      invalidDateRows: 0,
    };
  }

  const rows: StockyStocktakeListMetadata[] = [];
  const seenStockyNumbers = new Set<string>();
  const submittedHeaderText = lines[headerLine] ?? "";
  let skippedRows = 0;
  let invalidDateRows = 0;
  for (let index = headerLine + 1; index < lines.length; index += 1) {
    const submittedRowText = lines[index] ?? "";
    if (!submittedRowText.trim()) continue;
    if (rows.length >= MAX_ROWS) {
      return { rows: [], error: "too_many_rows", skippedRows, invalidDateRows };
    }
    const cells = submittedRowText.split("\t");
    const value = (kind: RequiredHeader) =>
      (cells[headerIndexes[kind]] ?? "").replace(/\u00a0/g, " ").trim();
    const stockyNumber = value("stockyNumber");
    const name = value("name");
    const locationName = value("locationName");
    const generatedAtRaw = value("generatedAt");
    const status = value("status");
    // No. と日時が無い行は表のフッターやページ文言として扱い、意味を推測しない。
    if (!stockyNumber || !generatedAtRaw) {
      skippedRows += 1;
      continue;
    }
    const stockyNumberKey = stockyNumber.normalize("NFKC");
    if (seenStockyNumbers.has(stockyNumberKey)) {
      return {
        rows: [],
        error: "duplicate_stocky_number",
        skippedRows,
        invalidDateRows,
      };
    }
    seenStockyNumbers.add(stockyNumberKey);
    const normalized = normalizeStockyGeneratedAt(generatedAtRaw);
    if (!normalized) invalidDateRows += 1;
    rows.push({
      id: `${index}:${stockyNumber}:${generatedAtRaw}`,
      stockyNumber,
      name,
      locationName,
      generatedAtRaw,
      generatedAtLocal: normalized?.generatedAtLocal ?? null,
      generatedOn: normalized?.generatedOn ?? null,
      status,
      submittedHeaderText,
      submittedRowText,
    });
  }

  return {
    rows,
    error: rows.length === 0 ? "no_data_rows" : null,
    skippedRows,
    invalidDateRows,
  };
}
